import { CheckmarkOutline16, Copy16 } from '@carbon/icons-react';
import Add16 from '@carbon/icons-react/es/add/16';
import Launch16 from '@carbon/icons-react/es/launch/16';
import WebHook32 from '@carbon/icons-react/es/webhook/32';
import { Space as AntSpace, Typography, Row as AntRow, Col } from 'antd';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import styled from 'styled-components';
import { APIKeysApi, ApiKey, CreateApiKeyPayload } from 'api/api-keys';
import { Button as AntButton } from 'components/atoms/button';
import { EmptyState, EmptyStateContainer } from 'components/atoms/empty-state';
import { Loading as AntLoading } from 'components/atoms/loading';
import { TableSkeleton } from 'components/atoms/table';
import { ActionBar } from 'components/molecules/action-bar';
import { Table as UnstyledTable } from 'components/molecules/table';
import { toastService } from 'components/molecules/toast';
import { humanise } from 'components/pages/timesheet-settings/lock-time-settings';
import { SingleLineEllipsis } from 'components/styles';
import { useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import { GET_RL_KEY, useGetRLApiKey } from 'queries/api-keys';
import { MoreOptionsRenderer } from './more-options-renderer';
import { NewApiKeyModal } from './new-api-key-modal';

const Space = styled(AntSpace).attrs({ direction: 'vertical' })`
  display: block;
  .ant-space-item {
    margin: 0 !important;
  }
`;

const StyledActionBar = styled(ActionBar)`
  background: var(--white-100);
  padding: var(--spacing-03) var(--spacing-06);
  && .ant-btn {
    min-width: auto;
    width: auto;
  }
`;

const Loading = styled(AntLoading)`
  padding: var(--spacing-06);
`;

const Table = styled(UnstyledTable)`
  padding: 0 var(--spacing-06);

  .ant-table-row {
    height: var(--spacing-09);
  }

  .ant-table-cell {
    padding: 0 var(--spacing-05);
  }

  .ant-col:nth-child(2) {
    display: none;
  }
  &:hover .ant-col:nth-child(2) {
    display: flex;
  }
`;

const Cell = styled.div`
  p {
    width: calc(100% - 32px);
    margin-bottom: 0px;
    ${SingleLineEllipsis};
  }

  display: flex;
  padding: var(--spacing-05) 0px;
  justify-content: space-between;
  align-items: center;
  height: 50px;
`;

const Button = styled(AntButton)`
  margin-top: var(--spacing-06);
`;

const IconWrapper = styled.div`
  color: var(--coolest-gray-40);
`;

const EmptyStateTitle = styled(FormattedMessage)`
  margin: var(--spacing-04) 0 !important;
`;

const CopyButton = styled(AntButton)`
  background: transparent;
`;

const CopyIcon = styled(Copy16)`
  color: var(--black-100);

  &:hover {
    color: var(--blue-60);
  }
`;

const Row = styled(AntRow)``;

const columns = [
  {
    title: <FormattedMessage id="settings.api.key.name" />,
    dataIndex: 'apiKeyName',
  },
  {
    title: <FormattedMessage id="settings.api.key" />,
    dataIndex: 'apiKey',
    render: (val: string) => (
      <Row align="middle" justify="space-between">
        <Col>{val}</Col>
        <Col>
          <CopyButton type="text" size="small">
            <Typography.Text
              copyable={{
                text: val,
                icon: [<CopyIcon />, <CheckmarkOutline16 />],
                tooltips: [
                  <FormattedMessage id="click_to_copy" />,
                  <FormattedMessage id="copied" />,
                ],
              }}
            />
          </CopyButton>
        </Col>
      </Row>
    ),
  },
  {
    title: <FormattedMessage id="template.table.header.created.on" />,
    dataIndex: 'createdAt',
    width: '15%',
    render: (val: string) => (
      <Cell>
        <p>{humanise(moment(val))}</p>
      </Cell>
    ),
  },
  {
    dataIndex: 'apiKey',
    width: '15%',
    render: (val: string) => (
      <Row>
        <Col />
        <Col>
          <AntButton
            type="tertiary"
            icon={<Launch16 />}
            iconPlacement="right"
            onClick={async () => {
              const readmeJWT = await APIKeysApi.getJWTForAPIKey(val);

              window.open(
                `https://rocketlanev4.readme.io/?auth_token=${readmeJWT}`,
                '_blank',
              );
            }}
          >
            <FormattedMessage id="settings.try.api" />
          </AntButton>
        </Col>
      </Row>
    ),
  },
  {
    title: null,
    dataIndex: 'apiKeyId',
    width: '5%',
    render: (apiKeyId: number, record: ApiKey) => (
      <Cell>
        <MoreOptionsRenderer
          apiKeyId={apiKeyId}
          apiKeyName={record.apiKeyName}
        />
      </Cell>
    ),
  },
];

export const RocketlaneApi = () => {
  const { data, isFetching } = useGetRLApiKey();
  const { createRLApiKey } = APIKeysApi;
  const isEmpty = useMemo(() => data?.length === 0, [data]);

  const createFunction = (payload: CreateApiKeyPayload) =>
    createRLApiKey(payload);
  const { mutate, isLoading: isCreating } = useMutation(createFunction);
  const translate = useTranslate();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const onSubmit = (apiKeyName: string) => {
    mutate(
      {
        apiKeyName,
      },
      {
        onSuccess: async () => {
          setIsModalVisible(false);
          await queryClient.refetchQueries(GET_RL_KEY);
          toastService({
            title: translate('settings.api.api_key_created'),
            message: (
              <p>{translate('settings.api.api_key_created_description')}</p>
            ),
            duration: 5,
          }).success();
        },
      },
    );
  };

  return (
    <Space size="large">
      {isFetching ? (
        <Loading />
      ) : isEmpty ? (
        <EmptyStateContainer>
          <EmptyState
            image={
              <IconWrapper>
                <WebHook32 style={{ scale: '2.0' }} />
              </IconWrapper>
            }
            title={<EmptyStateTitle id="settings.api.empty_state.title" />}
            message={
              <FormattedMessage id="settings.api.empty_state.description" />
            }
            primaryCTA={
              <Button
                onClick={() => setIsModalVisible(true)}
                icon={<Add16 />}
                iconPlacement="right"
                type="primary"
              >
                <FormattedMessage id="settings.create.api.key" />
              </Button>
            }
          />
        </EmptyStateContainer>
      ) : (
        // <APIEmptyState setCreateModalVisibility={setIsModalVisible} />
        <>
          <StyledActionBar>
            <ActionBar.Secondary>
              <AntButton
                onClick={() => setIsModalVisible(true)}
                icon={<Add16 />}
                iconPlacement="right"
                type="primary"
              >
                <FormattedMessage id="settings.create.api.key" />
              </AntButton>
            </ActionBar.Secondary>
          </StyledActionBar>

          {isFetching ? (
            <TableSkeleton />
          ) : (
            <Table
              tableLayout="fixed"
              columns={columns}
              dataSource={data}
              pagination={false}
            />
          )}
        </>
      )}

      <NewApiKeyModal
        submit={onSubmit}
        visible={isModalVisible}
        closeModal={() => setIsModalVisible(false)}
        isCreating={isCreating}
        keys={data}
      />
    </Space>
  );
};
